import { useState } from "react";
import { endpoints } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Check, X, UserSearch } from "lucide-react";
import { toast } from "sonner";

export default function BuyerResolver() {
  const [id, setId] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const resolve = async () => {
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const r = await endpoints.resolveBuyer(id);
      setResult(r);
      toast.success("Buyer resolved");
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to resolve");
    } finally {
      setLoading(false);
    }
  };

  const isValidFormat = id.length === 12 && /^\d+$/.test(id);

  return (
    <div className="max-w-[900px] px-12 py-14" data-testid="page-buyer">
      <div className="tx-overline mb-2">Module M2</div>
      <h1 className="font-heading font-black text-4xl mb-2">Buyer Account Resolver</h1>
      <p className="text-zinc-600 text-[14px] max-w-2xl mb-8">
        Validate a 12-digit AWS Account ID against CRM, ACE and MCP hint sources
        before locking it into an authorization spec.
      </p>

      <div className="border border-zinc-200 bg-zinc-50 p-8 space-y-5">
        <div>
          <Label className="tx-overline">AWS Account ID</Label>
          <div className="mt-2 flex gap-3">
            <div className="relative flex-1">
              <Input
                data-testid="buyer-input"
                value={id}
                onChange={(e) => setId(e.target.value.replace(/\D/g, "").slice(0, 12))}
                placeholder="482910573361"
                className="bg-white border-zinc-200 font-mono text-[15px] tracking-widest h-12 rounded-sm pr-10 focus-visible:ring-orange-500/40"
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {id.length === 0 ? null : isValidFormat ? (
                  <Check className="w-4 h-4 text-emerald-600" />
                ) : (
                  <X className="w-4 h-4 text-rose-600" />
                )}
              </div>
            </div>
            <Button
              onClick={resolve}
              disabled={!isValidFormat || loading}
              data-testid="resolve-button"
              className="h-12 px-5 bg-zinc-900 text-white hover:bg-zinc-700 disabled:opacity-40 rounded-sm gap-2"
            >
              <UserSearch className="w-4 h-4" />
              Resolve
            </Button>
          </div>
          <div className="mt-2 text-[11px] text-zinc-500 font-mono">
            {id.length}/12 digits · numeric only
          </div>
        </div>

        {error && (
          <div className="border border-rose-500/40 bg-rose-500/5 p-4 text-[13px] text-rose-600">
            {error}
          </div>
        )}

        {result && (
          <div className="border border-emerald-500/40 bg-emerald-500/5 p-5 space-y-3 tx-fade-in">
            <div className="flex items-center gap-2 text-emerald-700">
              <Check className="w-4 h-4" />
              <span className="font-heading font-medium">Valid AWS Account</span>
            </div>
            <dl className="grid grid-cols-2 gap-x-8 gap-y-2 text-[13px]">
              <div>
                <dt className="tx-overline">Account ID</dt>
                <dd className="font-mono text-zinc-900">{result.aws_account_id}</dd>
              </div>
              <div>
                <dt className="tx-overline">Company</dt>
                <dd className="text-zinc-900">{result.company_name}</dd>
              </div>
              <div>
                <dt className="tx-overline">Primary contact</dt>
                <dd className="text-zinc-800">{result.primary_contact || "—"}</dd>
              </div>
              <div>
                <dt className="tx-overline">Reseller preference</dt>
                <dd className="text-zinc-800">{result.reseller_preference || "—"}</dd>
              </div>
              <div>
                <dt className="tx-overline">Matched opportunity</dt>
                <dd className="font-mono text-zinc-800">{result.matched_opportunity || "—"}</dd>
              </div>
            </dl>
            {result.notes && (
              <div className="text-[12px] text-zinc-600 border-t border-zinc-200 pt-3">
                {result.notes}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
